import type { ReactNode } from "react";
import { Link, useLocation } from "wouter";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/hooks/useAuth";
import { canAccessAdminSection, getAdminSectionByPath, isAdministrator } from "@/lib/adminAccess";
import { withAuthReturn } from "@/lib/authReturn";

export default function AdminPageGate({ children }: { children: ReactNode }) {
  const { user, isLoading } = useAuth();
  const [location] = useLocation();
  const section = getAdminSectionByPath(location);

  if (isLoading) return <div className="container mx-auto py-6">Checking administrator access…</div>;

  if (!user) {
    return (
      <div className="container mx-auto max-w-lg py-6">
        <Card>
          <CardHeader><CardTitle>Sign in required</CardTitle></CardHeader>
          <CardContent>
            <p className="mb-4 text-muted-foreground">Sign in to your administrator account to continue.</p>
            <Link href={withAuthReturn("/login", location)}><Button>Sign in</Button></Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Root dashboard redirects role-limited administrators to an allowed section.
  if (!isAdministrator(user) || (section && section.slug !== "" && !canAccessAdminSection(user, section))) {
    return (
      <div className="container mx-auto max-w-lg py-6">
        <Card>
          <CardHeader><CardTitle>Not authorized</CardTitle></CardHeader>
          <CardContent>
            <p className="text-muted-foreground">Your administrator roles do not include access to this page.</p>
            <Link href="/home"><Button className="mt-4" variant="outline">Back home</Button></Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  return <>{children}</>;
}
